import { ArrowLeftRight } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  liftHalfChipLabel,
  liftHalfChipTooltip,
} from "@/features/studies/questionnaire/assets/liftTags";

const PILL_BASE_CLASS =
  "inline-flex items-center gap-1 rounded-full border px-[10px] py-[3px] text-[11px] font-medium leading-[1.4] transition-colors";

interface LiftHalfChipProps {
  half: "pre" | "post";
  /** Caller-owned, because the builder keys by question id and the report by SurveyJS name. */
  testId: string;
  className?: string;
}

/**
 * The pre/post half of a within-subject lift pair, as one chip.
 *
 * Single source of truth for the two surfaces that show it: the builder's
 * QuestionnaireQuestionCard header and the report's Survey Data card. They
 * read the same `lift:pre:*` / `lift:post:*` tags from different shapes
 * (draft `tags` vs compiled `x_dt_tags`), so parsing stays at the call site
 * and only the presentation is shared.
 *
 * Muted slate rather than the primary monadic chips: same density, distinct
 * role (a before/after measure, not lane routing).
 */
export function LiftHalfChip(props: LiftHalfChipProps) {
  const { half, testId, className } = props;

  const label = liftHalfChipLabel(half);

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              PILL_BASE_CLASS,
              "cursor-default border-border bg-muted text-foreground",
              className,
            )}
            data-testid={testId}
            aria-label={label}
          >
            <ArrowLeftRight className="h-3 w-3" />
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px]">
          <span className="text-[11px]">{liftHalfChipTooltip(half)}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
